import { Link } from "@tanstack/react-router";
import { Bell, CheckCheck, MessageSquare, Wallet, Sparkles, Receipt } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useNotifications } from "@/hooks/use-notifications";
import { formatDistanceToNow } from "date-fns";
import { useT } from "@/i18n/useT";
import { useTranslation } from "react-i18next";
import { ar as arLocale, enUS } from "date-fns/locale";

const iconFor: Record<string, typeof Bell> = {
  message: MessageSquare,
  escrow: Wallet,
  invoice: Receipt,
  project_update: Sparkles,
};

export function NotificationBell() {
  const { items, unreadCount, markRead, markAllRead } = useNotifications();
  const t = useT();
  const { i18n } = useTranslation();
  const isAr = (i18n.language || "en").startsWith("ar");
  const locale = isAr ? arLocale : enUS;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" aria-label={t("Notifications", "الإشعارات")}>
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <>
              <span className="absolute end-1 top-1 h-2.5 w-2.5 rounded-full bg-primary shadow-[0_0_10px_var(--primary)]" />
              <span className="absolute end-1 top-1 h-2.5 w-2.5 animate-ping rounded-full bg-primary/70" />
              <span className="absolute -end-1 -top-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground shadow-[0_0_12px_var(--primary)]">
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 overflow-hidden">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <div className="text-sm font-semibold">{t("Notifications", "الإشعارات")}</div>
          {unreadCount > 0 && (
            <button
              onClick={markAllRead}
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            >
              <CheckCheck className="h-3 w-3" /> {t("Mark all read", "تعليم الكل كمقروء")}
            </button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-muted-foreground">
              {t("You're all caught up.", "لقد اطّلعت على كل الإشعارات.")}
            </div>
          ) : (
            items.map((n) => {
              const Icon = iconFor[n.kind] ?? Bell;
              const body = (
                <div
                  className={cn(
                    "flex gap-3 px-4 py-3 text-start transition-colors hover:bg-muted/50",
                    !n.read_at && "bg-primary/5",
                  )}
                >
                  <div
                    className={cn(
                      "mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full",
                      n.read_at ? "bg-muted text-muted-foreground" : "bg-primary/15 text-primary",
                    )}
                  >
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start gap-2">
                      <p className="flex-1 truncate text-sm font-medium">{n.title}</p>
                      {!n.read_at && <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />}
                    </div>
                    {n.body && <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{n.body}</p>}
                    <p className="mt-1 text-[10px] uppercase tracking-wider text-muted-foreground/70">
                      {formatDistanceToNow(new Date(n.created_at), { addSuffix: true, locale })}
                    </p>
                  </div>
                </div>
              );
              const onClick = () => {
                if (!n.read_at) void markRead(n.id);
              };
              if (n.link) {
                return (
                  <Link key={n.id} to={n.link} onClick={onClick} className="block border-b last:border-b-0">
                    {body}
                  </Link>
                );
              }
              return (
                <button
                  key={n.id}
                  onClick={onClick}
                  className="block w-full border-b last:border-b-0"
                  type="button"
                >
                  {body}
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
